import React, { useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useAdminData } from '../../hooks/useAdminData';
import { Case } from '../../types';
import CaseForm from '../Cases/CaseForm';
import ReassignCaseDialog from '../Cases/ReassignCaseDialog';
import SessionReport from '../Cases/SessionReport';
import CaseTimelineDialog from '../Cases/CaseTimelineDialog';
import CaseProfileDialog from '../Cases/CaseProfileDialog';
import AdminPageHeader from './AdminPageHeader';
import AdminTabs from './AdminTabs';
import AdminUsersPanel from './AdminUsersPanel';
import AdminCasesPanel from './AdminCasesPanel';
import AdminReceivedReportsPanel from './AdminReceivedReportsPanel';
import AdminConsentsPanel from './AdminConsentsPanel';
import AdminMentorshipPanel from './AdminMentorshipPanel';
import AdminUserDialogs from './AdminUserDialogs';
import ConfirmDialog from '../common/ConfirmDialog';
import { emptyCreateUserData } from './adminUtils';
import { t } from '../../utils/translations';

type PendingConfirm =
  | { type: 'deleteUser'; userId: string; message: string }
  | { type: 'deactivateUser'; userId: string }
  | { type: 'transferSupremeLeader'; userId: string };

const AdminTools: React.FC = () => {
  const data = useAdminData();
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [timelineCase, setTimelineCase] = useState<Case | null>(null);
  const [profileCase, setProfileCase] = useState<Case | null>(null);

  const handleConfirmAction = async () => {
    if (!pendingConfirm) return;

    try {
      setConfirmLoading(true);
      if (pendingConfirm.type === 'deleteUser') {
        await data.handleDeleteUser(pendingConfirm.userId);
      } else if (pendingConfirm.type === 'transferSupremeLeader') {
        await data.handleTransferSupremeLeader(pendingConfirm.userId);
      } else {
        await data.handleDeactivateUser(pendingConfirm.userId);
      }
      setPendingConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  if (!data.canManageUsers) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        Nu ai permisiunea de a accesa uneltele admin.
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader />
      <AdminTabs
        activeTab={data.activeTab}
        onTabChange={data.setTab}
        showLeaderExtras={data.currentUser?.role === 'leader'}
        showMentorship={data.isSupremeLeader}
      />

      {data.activeTab === 0 && (
        <AdminUsersPanel
          users={data.filteredUsers ?? data.users}
          counselors={data.counselors}
          loading={data.usersLoading}
          canCreateUsers={data.canCreateUsers}
          currentUserId={data.currentUser?.id}
          currentUserRole={data.currentUser?.role}
          isSupremeLeader={data.isSupremeLeader}
          workloadFilter={data.userWorkloadFilter}
          onWorkloadFilterChange={data.setUserWorkloadFilter}
          workloadCounts={data.userWorkloadCounts}
          onCreateUser={data.openCreateUserDialog}
          onEdit={data.openEditDialog}
          onDeactivate={(userId) => setPendingConfirm({ type: 'deactivateUser', userId })}
          onReactivate={data.handleReactivateUser}
          reactivatingUserId={data.reactivatingUserId}
          onDelete={(userId) => {
            const isDeletingSelf = userId === data.currentUser?.id;
            setPendingConfirm({
              type: 'deleteUser',
              userId,
              message: isDeletingSelf
                ? t.admin.users.deleteUserSelfConfirm
                : t.admin.users.deleteUserConfirm,
            });
          }}
          onTransferSupremeLeader={(userId) =>
            setPendingConfirm({ type: 'transferSupremeLeader', userId })
          }
        />
      )}

      

      {data.activeTab === 1 && (
        <AdminCasesPanel
          loading={data.casesLoading}
          error={data.casesError}
          cases={data.filteredCases}
          counselors={data.counselors}
          searchTerm={data.caseSearchTerm}
          onSearchChange={data.setCaseSearchTerm}
          statusFilter={data.caseStatusFilter}
          onStatusFilterChange={data.setCaseStatusFilter}
          statusCounts={data.caseStatusCounts}
          counselorFilter={data.caseCounselorFilter}
          onCounselorFilterChange={data.setCaseCounselorFilter}
          onAdd={() => {
            data.setEditingCase(null);
            data.setCaseFormOpen(true);
          }}
          onEdit={(caseItem) => {
            data.setEditingCase(caseItem);
            data.setCaseFormOpen(true);
          }}
          onReassign={(caseItem) => {
            data.setReassigningCase(caseItem);
          }}
          onDelete={data.handleDeleteCase}
          onOpenSessionReport={data.handleOpenSessionReport}
          onOpenTimeline={setTimelineCase}
          onOpenDescription={setProfileCase}
          canViewReports={data.currentUser?.role === 'leader'}
          blurSensitiveContent={data.currentUser?.role === 'admin'}
        />
      )}

      {data.activeTab === 2 && data.currentUser?.role === 'leader' && (
        <AdminReceivedReportsPanel />
      )}

      {data.activeTab === 3 && data.currentUser?.role === 'leader' && (
        <AdminConsentsPanel />
      )}

      {data.activeTab === 4 && data.isSupremeLeader && (
        <AdminMentorshipPanel
          users={data.users}
          counselors={data.counselors}
          allCases={data.allCases}
          currentUserId={data.currentUser?.id}
          currentUserName={data.currentUser?.fullName}
          onUsersChanged={data.loadUsers}
        />
      )}

      <ConfirmDialog
        open={pendingConfirm !== null}
        title={
          pendingConfirm?.type === 'deleteUser'
            ? t.admin.users.deleteUserTitle
            : pendingConfirm?.type === 'transferSupremeLeader'
              ? t.admin.users.transferSupremeLeaderTitle
              : t.admin.users.deactivateUser
        }
        message={
          pendingConfirm?.type === 'deleteUser'
            ? pendingConfirm.message
            : pendingConfirm?.type === 'transferSupremeLeader'
              ? t.admin.users.transferSupremeLeaderConfirm
              : t.admin.users.deactivateUserConfirm
        }
        variant={pendingConfirm?.type === 'deleteUser' ? 'danger' : 'default'}
        confirmLabel={
          pendingConfirm?.type === 'deleteUser'
            ? t.common.delete
            : pendingConfirm?.type === 'transferSupremeLeader'
              ? t.admin.users.transferSupremeLeader
              : t.admin.users.deactivateUser
        }
        loading={confirmLoading}
        onClose={() => {
          if (!confirmLoading) setPendingConfirm(null);
        }}
        onConfirm={handleConfirmAction}
      />

      <AdminUserDialogs
        createDialogOpen={data.createDialogOpen}
        editDialogOpen={data.editDialogOpen}
        createUserLoading={data.createUserLoading}
        editUserLoading={data.editUserLoading}
        createUserData={data.createUserData}
        editUserData={data.editUserData}
        selectedUser={data.selectedUser}
        currentUserRole={data.currentUser?.role}
        onCloseCreate={() => {
          data.closeCreateUserDialog();
          if (!data.createUserLoading) {
            data.setCreateUserData(emptyCreateUserData());
          }
        }}
        onCloseEdit={() => {
          if (data.editUserLoading) return;
          data.setEditDialogOpen(false);
        }}
        onCreateUserDataChange={data.setCreateUserData}
        onEditUserDataChange={data.setEditUserData}
        onCreateUser={data.handleCreateUser}
        onEditUser={data.handleEditUser}
        onCopyCredentials={data.copyUserCredentials}
      />

      <CaseForm
        open={data.caseFormOpen}
        onClose={data.handleCloseCaseForm}
        onSubmit={data.handleCaseSubmit}
        caseData={data.editingCase}
        counselors={data.counselors}
        inactiveUserIds={data.users.filter((u) => !u.isActive).map((u) => u.id)}
        restrictSensitiveContent={data.currentUser?.role === 'admin'}
      />

      <ReassignCaseDialog
        open={data.reassigningCase !== null}
        caseItem={data.reassigningCase}
        counselors={data.counselors}
        inactiveUserIds={data.users.filter((u) => !u.isActive).map((u) => u.id)}
        onClose={() => data.setReassigningCase(null)}
        onSubmit={async (assignment) => {
          if (!data.reassigningCase) return;
          await data.handleReassignCase(data.reassigningCase, assignment);
        }}
      />

      <SessionReport
        open={data.currentUser?.role === 'leader' && data.sessionReportOpen}
        onClose={data.handleCloseSessionReport}
        caseId={data.selectedCaseForSessionReport?.id || ''}
        caseTitle={data.selectedCaseForSessionReport?.title || ''}
        onReportAdded={() => undefined}
        hideAddButton
        caseStatus={data.selectedCaseForSessionReport?.status}
        restrictSensitiveContent={data.currentUser?.role === 'admin'}
      />

      <CaseProfileDialog
        open={profileCase !== null}
        caseItem={profileCase}
        onClose={() => setProfileCase(null)}
        onSaved={() => void data.loadCases()}
      />

      <CaseTimelineDialog
        open={timelineCase !== null}
        onClose={() => setTimelineCase(null)}
        caseItem={timelineCase}
        includeMeetingNotes={false}
        restrictSessionReports={data.currentUser?.role === 'admin'}
      />

      <Snackbar
        open={data.snackbar.open}
        autoHideDuration={6000}
        onClose={() => data.setSnackbar({ ...data.snackbar, open: false })}
      >
        <Alert
          onClose={() => data.setSnackbar({ ...data.snackbar, open: false })}
          severity={data.snackbar.severity}
          sx={{ width: '100%' }}
          className="rounded-xl"
        >
          {data.snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminTools;
